/// <reference types="google.maps" />
/* eslint-disable no-undef */
/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
/* eslint-disable no-use-before-define */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { InputLabelProps, InputProps } from '@material-ui/core';

// ─── Types ────────────────────────────────────────────────────────────────────

// Wraps the new API's PlacePrediction so the rest of the component stays compatible
export interface PlaceType {
    description: string;       // formattedText (full suggestion string)
    place_id: string;           // filled after toPlace() – we store it up front
    structured_formatting: {
        main_text: string;
        secondary_text: string;
        main_text_matched_substrings: { offset: number; length: number }[];
    };
    // Keep a reference to the raw prediction for fetchFields()
    _prediction?: google.maps.places.PlacePrediction;
}

export interface Address {
    premise: string;
    subPremise: string;
    floor: string;
    streetNumber: string;
    route: string;
    locality: string;
    sublocality: string;
    administrativeAreaLevel1: string;
    administrativeAreaLevel2: string;
    administrativeAreaLevel3: string;
    country: string;
    postalCode: string;
}

interface AddressSelectorProps {
    label?: string;
    required?: boolean;
    helperText?: string;
    error?: boolean;
    className?: string;
    placeTypes?: string[];
    name?: string;
    freeSolo?: boolean;
    homeCountry?: string;
    onSelection?: (placeType: PlaceType | null, address: Address, name: string) => void;
    onInputChanged?: (value: string) => void;
    // tslint:disable-next-line: no-any
    value?: any;
    size?: 'small' | 'medium';
    variant?: 'standard' | 'outlined' | 'filled';
    InputLabelProps?: Partial<InputLabelProps>;
    InputProps?: Partial<InputProps>;
    disabled?: boolean;
}

interface AddressSelectorState extends Address {
    error?: boolean;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    icon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(2),
    },
}));

// ─── Google Maps loader (replaces the old <script> tag approach) ──────────────

// Maps is loaded globally via the <script> tag in index.html.
// This helper simply waits for window.google to become available.
function loadGoogleMaps(_apiKey?: string): Promise<void> {
    if ((window as any).google?.maps?.places) return Promise.resolve();

    return new Promise((resolve) => {
        const interval = setInterval(() => {
            if ((window as any).google?.maps?.places) {
                clearInterval(interval);
                resolve();
            }
        }, 100);
    });
}

// ─── Helper: map new AddressComponent[] → Address ────────────────────────────

function mapComponents(components: google.maps.places.AddressComponent[]): Address {
    const sel: Address = {
        premise: '', subPremise: '', floor: '', streetNumber: '',
        route: '', locality: '', sublocality: '',
        administrativeAreaLevel1: '', administrativeAreaLevel2: '',
        administrativeAreaLevel3: '', country: '', postalCode: '',
    };

    for (const c of components) {
        const types: string[] = (c as any).types ?? [];

        // The new API exposes longText / shortText instead of long_name / short_name
        const short: string = (c as any).shortText ?? (c as any).short_name ?? '';
        const long: string  = (c as any).longText  ?? (c as any).long_name  ?? '';

        if (types.includes('premise'))                    sel.premise                 ||= short;
        if (types.includes('subpremise'))                 sel.subPremise              ||= short;
        if (types.includes('floor'))                      sel.floor                   ||= short;
        if (types.includes('street_number'))              sel.streetNumber            ||= short;
        if (types.includes('route'))                      sel.route                   ||= long;
        if (types.includes('locality'))                   sel.locality                ||= short;
        if (types.includes('sublocality'))                sel.sublocality             ||= short;
        if (types.includes('administrative_area_level_1')) sel.administrativeAreaLevel1 ||= short;
        if (types.includes('administrative_area_level_2')) sel.administrativeAreaLevel2 ||= short;
        if (types.includes('administrative_area_level_3')) sel.administrativeAreaLevel3 ||= short;
        if (types.includes('country'))                    sel.country                 ||= long;
        if (types.includes('postal_code'))                sel.postalCode              ||= long;
    }
    return sel;
}

// ─── Helper: map PlacePrediction → PlaceType (our compat shape) ───────────────

function predictionToPlaceType(pred: google.maps.places.PlacePrediction): PlaceType {
    // The new API uses FormattableText objects – call .toString() to get the string
    const mainText      = (pred as any).mainText?.toString()      ?? '';
    const secondaryText = (pred as any).secondaryText?.toString() ?? '';
    const fullText      = (pred as any).text?.toString()          ?? `${mainText}, ${secondaryText}`;

    // mainText matched substrings (may not always be present in new API)
    const rawMatches: { startOffset: number; endOffset: number }[] =
        (pred as any).mainText?.matches ?? [];
    const matches = rawMatches.map((m) => ({
        offset: m.startOffset ?? 0,
        length: (m.endOffset ?? 0) - (m.startOffset ?? 0),
    }));

    return {
        description: fullText,
        place_id: (pred as any).placeId ?? '',
        structured_formatting: {
            main_text: mainText,
            secondary_text: secondaryText,
            main_text_matched_substrings: matches,
        },
        _prediction: pred,
    };
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AddressSelector: React.FC<AddressSelectorProps & { apiKey?: string }> = (props) => {
    const classes = useStyles();

    const [value, setValue]           = useState<PlaceType | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions]       = useState<PlaceType[]>([]);

    const [state, setState] = useState<AddressSelectorState>({
        error: props.error,
        premise: '', subPremise: '', floor: '', streetNumber: '',
        route: '', locality: '', sublocality: '',
        administrativeAreaLevel1: '', administrativeAreaLevel2: '',
        administrativeAreaLevel3: '', country: '', postalCode: '',
    });

    // Session token groups autocomplete + detail calls into one billing event
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

    // ── Sync external props ──────────────────────────────────────────────────

    useEffect(() => {
        setState((prev) => ({ ...prev, error: props.error }));
    }, [props.error]);

    useEffect(() => {
        setValue(props.value ?? null);
    }, [props.value]);

    // ── Throttled suggestion fetcher ─────────────────────────────────────────

    const fetchSuggestions = useCallback(
        throttle(async (input: string) => {
            if (!input) { setOptions(value ? [value] : []); return; }

            await loadGoogleMaps(props.apiKey);

            const g = (window as any).google;
            if (!g?.maps?.places?.AutocompleteSuggestion) return;

            // Create a fresh session token if we don't have one yet
            if (!sessionTokenRef.current) {
                sessionTokenRef.current = new g.maps.places.AutocompleteSessionToken();
            }

            try {
                const request: Record<string, unknown> = {
                    input,
                    sessionToken: sessionTokenRef.current,
                };
                if (props.placeTypes?.length)  request.includedPrimaryTypes  = props.placeTypes;
                // if homeCountry is empty default to au
                if (props.homeCountry) {
                    request.includedRegionCodes   = [props.homeCountry];
                } else {
                    request.includedRegionCodes   = ['AU'];
                }                           

                const { suggestions } =
                    await g.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

                const mapped: PlaceType[] = (suggestions as any[])
                    .map((s) => predictionToPlaceType(s.placePrediction));

                setOptions(value ? [value, ...mapped] : mapped);
            } catch (err) {
                console.error('AutocompleteSuggestion error:', err);
            }
        }, 200),
        [props.apiKey, props.placeTypes, props.homeCountry, value],
    );

    // ── Re-fetch whenever input changes ──────────────────────────────────────

    useEffect(() => {
        fetchSuggestions(inputValue);
        // Cleanup: cancel in-flight throttled call on unmount
        return () => { fetchSuggestions.cancel?.(); };
    }, [inputValue, fetchSuggestions]);

    // ── Handle selection ─────────────────────────────────────────────────────

    const onSuggestionSelected = async (suggestion: PlaceType) => {
        const pred = suggestion._prediction;
        if (!pred) return;

        try {
            await loadGoogleMaps(props.apiKey);

            // toPlace() + fetchFields() replaces PlacesService.getDetails()
            const place = (pred as any).toPlace();
            await place.fetchFields({ fields: ['addressComponents'] });

            // Reset session token – this finalises the billing session
            sessionTokenRef.current = null;

            const components: google.maps.places.AddressComponent[] =
                place.addressComponents ?? [];

            if (components.length && props.onSelection) {
                const address = mapComponents(components);
                props.onSelection(suggestion, address, props.name ?? 'address');
            }
        } catch (err) {
            console.error('Place fetchFields error:', err);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <Autocomplete
            freeSolo={props.freeSolo}
            className={props.className}
            getOptionLabel={(option) =>
                typeof option === 'string'
                    ? option
                    : option.structured_formatting.main_text
            }
            filterOptions={(x) => x}   // API does the filtering – disable MUI's client filter
            options={options}
            includeInputInList
            filterSelectedOptions
            value={value}
            autoComplete={false}
            disabled={props.disabled}
            onChange={(event: React.ChangeEvent<{}>, newValue: string | PlaceType | null) => {
                const placeValue = typeof newValue === 'string' ? null : newValue;
                if (placeValue != null) {
                    onSuggestionSelected(placeValue);
                }
                setOptions(placeValue ? [placeValue, ...options] : options);
                setValue(placeValue);
            }}
            onInputChange={(event, newInputValue) => {
                if (props.onInputChanged) props.onInputChanged(newInputValue);
                setInputValue(newInputValue);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={props.label ?? 'Address'}
                    variant={props.variant ? props.variant : 'standard'}
                    fullWidth
                    margin="none"
                    required={props.required}
                    error={state.error}
                    helperText={props.helperText}
                    autoComplete="off"
                    size={props.size}
                    InputLabelProps={props.InputLabelProps}
                />
            )}
            renderOption={(option: string | PlaceType) => {
                if (typeof option === 'string' || !option?.structured_formatting) return null;

                const { main_text, secondary_text, main_text_matched_substrings: matches } =
                    option.structured_formatting;

                const parts = parse(
                    main_text,
                    matches.map((m) => [m.offset, m.offset + m.length]),
                );

                return (
                    <Grid container alignItems="center">
                        <Grid item>
                            <LocationOnIcon className={classes.icon} />
                        </Grid>
                        <Grid item xs>
                            {parts.map((part, index) => (
                                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                    {part.text}
                                </span>
                            ))}
                            <Typography variant="body2" color="textSecondary">
                                {secondary_text}
                            </Typography>
                        </Grid>
                    </Grid>
                );
            }}
        />
    );
};